import { inject } from '@angular/core';
import { PCP_LOGGER } from '@pcp/logging';

/**
 * The generic UI-interaction telemetry event.
 *
 * ⚠️ Both the event NAME and the property SHAPE are a contract with the service-side ETL:
 * `PCP.Function.LogIngestion/Models/LogAnalytics/UiInteractionEvent.cs` matches
 * `| where Name == 'UiInteraction'` and deserializes these exact property names into
 * `UiInteractionProps`. Renaming either end breaks a deployed Azure Function silently — no build
 * error, no test failure, just an ETL that stops finding events.
 *
 * Emit it through an **unscoped** `inject(PCP_LOGGER)`. A scoped logger prefixes the event name
 * with its domain (`drawing.UiInteraction`), which the exact-equality matcher would never see.
 *
 * See `docs/logging/telemetry-enrichment-contract.md`.
 */
export const UI_INTERACTION_EVENT = 'UiInteraction';

export interface UiInteractionProps {
    interactionGroup: string;
    interactionItem: string;
    interactionSubItem: string;
    interactionType: string;
    newValue: string | null;
    /** Free-form payload — callers pass whole domain objects (a video, a lection, a location). */
    additionalProperties?: unknown;

    /** `PcpLoggerInterface.trackEvent` takes a `Record<string, unknown>`; this keeps it assignable. */
    [key: string]: unknown;
}

/**
 * Builds the `UiInteraction` property bag.
 *
 * The event name is deliberately generic and identical for every interaction — these four fields,
 * not the name, are what distinguish one from another.
 */
export function uiInteractionProps(
    interactionGroup: string,
    interactionItem: string,
    interactionSubItem: string,
    interactionType: string,
    newValue: string | null = null,
    additionalProperties?: unknown,
): UiInteractionProps {
    return {
        interactionGroup,
        interactionItem,
        interactionSubItem,
        interactionType,
        newValue,
        additionalProperties,
    };
}

/**
 * Returns a function that emits a correctly-shaped, correctly-named `UiInteraction` event.
 *
 * **Call this from a field initializer**, not from inside a callback:
 *
 * ```ts
 * private readonly trackUiInteraction = injectUiInteractionTracker();
 * ...
 * tap(p => this.trackUiInteraction('Toolbar', 'DrawingTool', p.toolName, 'toggle'))
 * ```
 *
 * `inject()` needs an active injection context, which exists while a class's fields are being
 * initialized but not later — calling this directly inside a `tap()` throws `NG0203`. The factory
 * shape resolves the logger once and hands back a closure that is callable anywhere.
 *
 * Why a factory rather than spelling out `trackEvent` at each call site: the event name and the
 * unscoped logger are both part of the ETL contract (see above), and neither is a parameter here.
 * A caller cannot pass the wrong name, and cannot accidentally use a scoped logger that would
 * prefix it with a domain.
 */
export function injectUiInteractionTracker() {
    // UNSCOPED on purpose — a scoped logger would emit `<domain>.UiInteraction`.
    const log = inject(PCP_LOGGER);

    return (
        interactionGroup: string,
        interactionItem: string,
        interactionSubItem: string,
        interactionType: string,
        newValue: string | null = null,
        additionalProperties?: unknown,
    ): void => {
        log.trackEvent(
            UI_INTERACTION_EVENT,
            uiInteractionProps(
                interactionGroup, interactionItem, interactionSubItem,
                interactionType, newValue, additionalProperties,
            ),
        );
    };
}
